import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as NativeApp } from '@capacitor/app';
import { getLocalStorage, getUser } from '../utils/func';
import config from '../utils/config';
import { isStorageKey } from '../utils/storage';
import url from '../utils/url';
import { notificationRequest, notificationTarget } from './api';
import { createNativePush, isNativeIOS } from './nativePush';
import { createAndroidPush, isNativeAndroid, isAndroidApp } from './androidPush';
import { useOnboarding } from '../onboarding/OnboardingProvider';

const Context = createContext({ enabled: false, hasUnseen: false, refresh: async () => {}, enablePush: async () => {} });
export const useNotifications = () => useContext(Context);

export default function NotificationsProvider({ ready, children }) {
  const navigate = useNavigate();
  const { dismiss: dismissOnboarding } = useOnboarding();
  const [, setAuthVersion] = useState(0);
  const [capabilities, setCapabilities] = useState(null);
  const [hasUnseen, setHasUnseen] = useState(false);
  const [action, setAction] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState({ platform: isNativeIOS() ? 'ios' : isAndroidApp() ? 'android' : 'web',
    permission: 'unknown', registration: 'unavailable' });
  const native = useRef(null), refreshRef = useRef(null), epoch = useRef(0), statusRequest = useRef(null);
  const token = getLocalStorage(config.token), userId = getUser()?.id;
  const identity = useRef(null); identity.current = { token, userId };
  const notificationsEnabled = capabilities?.notifications === true;
  const nativeIOSAvailable = capabilities?.native_ios === true, nativeAndroidAvailable = capabilities?.native_android === true;

  useEffect(() => {
    const changed = event => {
      if (event.type === 'storage' && !isStorageKey(event, config.token) && !isStorageKey(event, config.user)) return;
      if (identity.current.token !== getLocalStorage(config.token) || identity.current.userId !== getUser()?.id) {
        setAuthVersion(value => value + 1);
      }
    };
    window.addEventListener('wiw:session-changed', changed); window.addEventListener('storage', changed);
    return () => { window.removeEventListener('wiw:session-changed', changed); window.removeEventListener('storage', changed); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let timer, pending = null;
    const load = () => {
      if (controller.signal.aborted || pending) return pending;
      pending = notificationRequest('/capabilities', { signal: controller.signal, token }).then(result => {
        if (!controller.signal.aborted) setCapabilities(previous => previous
          && ['notifications', 'native_ios', 'native_android'].every(key => previous[key] === result[key]) ? previous : result);
      }).catch(() => {
        if (!controller.signal.aborted) timer = setTimeout(load, 15000);
      }).finally(() => { pending = null; });
      return pending;
    };
    void load();
    const visible = () => { if (document.visibilityState !== 'hidden') { clearTimeout(timer); void load(); } };
    document.addEventListener('visibilitychange', visible); window.addEventListener('online', visible);
    return () => { clearTimeout(timer); controller.abort(); document.removeEventListener('visibilitychange', visible); window.removeEventListener('online', visible); };
  }, [token, userId]);

  const refresh = useCallback(async (afterChange = false) => {
    if (!ready || !token || !userId || !notificationsEnabled || document.visibilityState === 'hidden'
      || identity.current.token !== token || identity.current.userId !== userId) return;
    if (statusRequest.current?.token === token && statusRequest.current?.userId === userId) {
      // A receipt/seen write may be newer than the status already in flight.
      statusRequest.current.dirty ||= afterChange === true;
      return statusRequest.current.promise;
    }
    const current = epoch.current;
    const entry = { token, userId, controller: new AbortController(), promise: null };
    statusRequest.current = entry;
    entry.promise = notificationRequest('/status', { token, signal: entry.controller.signal }).then(result => {
      if (!entry.controller.signal.aborted && epoch.current === current && identity.current.token === token && identity.current.userId === userId) {
        setHasUnseen(Boolean(result.has_unseen));
        window.dispatchEvent(new Event('wiw:notifications-refresh'));
      }
    }).catch(() => { /* polling never interrupts the current page/editor */ })
      .finally(() => {
        if (statusRequest.current !== entry) return;
        statusRequest.current = null;
        if (entry.dirty && !entry.controller.signal.aborted) void refreshRef.current?.();
      });
    return entry.promise;
  }, [ready, token, userId, notificationsEnabled]);
  refreshRef.current = refresh;

  useEffect(() => {
    native.current = (isNativeIOS() ? createNativePush : createAndroidPush)(
      { onReceived: () => refreshRef.current?.(true), onAction: value => { dismissOnboarding(); setAction(value); }, onState: next => setDeviceStatus(previous =>
        Object.keys(next).every(key => previous[key] === next[key]) ? previous : next) });
    const instance = native.current;
    return () => { native.current = null; void instance.dispose(); };
  }, [dismissOnboarding]);

  useEffect(() => {
    const currentEpoch = ++epoch.current; setHasUnseen(false);
    const canRegister = isNativeIOS() ? nativeIOSAvailable : nativeAndroidAvailable;
    const session = ready && token && userId && canRegister ? { token, userId } : null;
    native.current?.setSession(session).catch(() => {});
    void native.current?.refreshStatus?.();
    void refresh();
    const visible = () => {
      if (document.visibilityState !== 'hidden') {
        void refresh(); void native.current?.refreshStatus?.(); native.current?.register(false).catch(() => {});
      }
    };
    const timer = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('online', visible);
    const listener = isNativeIOS() ? NativeApp.addListener('appStateChange', ({ isActive }) => { if (isActive) visible(); }) : null;
    return () => {
      epoch.current = currentEpoch + 1; clearInterval(timer); document.removeEventListener('visibilitychange', visible);
      statusRequest.current?.controller.abort(); statusRequest.current = null;
      window.removeEventListener('online', visible);
      listener?.then(handle => handle.remove()).catch(() => {});
    };
  }, [ready, token, userId, nativeIOSAvailable, nativeAndroidAvailable, refresh]);

  useEffect(() => {
    if (!action || !ready) return;
    if (!token || !userId) { navigate(`${url('/login')}?from=${encodeURIComponent(url('/notifications'))}`); return; }
    if (action.userId !== userId) { setAction(null); return; }
    let active = true;
    notificationRequest(`/${action.id}`, { token }).then(result => {
      if (!active) return;
      const target = notificationTarget(result.notification?.target);
      setAction(null); navigate(url(target || '/notifications'));
    }).catch(() => { if (active) { setAction(null); navigate(url('/notifications')); } });
    return () => { active = false; };
  }, [action, ready, token, userId, navigate]);

  const enablePush = useCallback(async () => {
    if (!isNativeIOS() && !isNativeAndroid() && !isAndroidApp()) return;
    if (isAndroidApp() && !isNativeAndroid()) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    if (!(isNativeIOS() ? nativeIOSAvailable : nativeAndroidAvailable)) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    const instance = native.current, owner = identity.current;
    if (!instance) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    try { await instance.register(true); }
    catch (error) {
      if (error.message !== 'PUSH_SETTINGS_REQUIRED') throw error;
      if (native.current !== instance || owner.token !== identity.current.token || owner.userId !== identity.current.userId) throw new Error('PUSH_SESSION_CHANGED');
      await instance.openSettings();
      return { settingsOpened: true };
    }
  }, [nativeIOSAvailable, nativeAndroidAvailable]);
  const refreshDeviceStatus = useCallback(() => native.current?.refreshStatus?.(), []);
  const openPushSettings = useCallback(() => native.current?.openSettings?.(), []);
  const available = deviceStatus.platform === 'ios' ? nativeIOSAvailable
    : deviceStatus.platform === 'android' && isNativeAndroid() && nativeAndroidAvailable;
  const value = useMemo(() => ({ enabled: notificationsEnabled, hasUnseen, refresh, enablePush,
    deviceStatus: { ...deviceStatus, available }, refreshDeviceStatus, openPushSettings }),
  [notificationsEnabled, hasUnseen, refresh, enablePush, deviceStatus, available, refreshDeviceStatus, openPushSettings]);
  return <Context.Provider value={value}>
    {children}
  </Context.Provider>;
}
